import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import accountApi from '../../services/accountApi';

function ApiKeysPage() {
  const { activeTenantId, hasTenantPermission } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newSecret, setNewSecret] = useState(null);

  const canCreate = hasTenantPermission('api_key.create');
  const canRevoke = hasTenantPermission('api_key.revoke');
  const canRotate = hasTenantPermission('api_key.rotate');

  useEffect(() => {
    if (!activeTenantId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await accountApi.listApiKeys(activeTenantId);
        if (!cancelled) setKeys(rows);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load API keys');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTenantId]);

  const reloadKeys = async () => {
    if (!activeTenantId) return;
    const rows = await accountApi.listApiKeys(activeTenantId);
    setKeys(rows);
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!canCreate || !activeTenantId) return;
    setCreating(true);
    setError(null);
    setNewSecret(null);
    try {
      const created = await accountApi.createApiKey(activeTenantId, { name: name || 'API Key' });
      if (created.secret) {
        setNewSecret(created.secret);
      }
      setName('');
      await reloadKeys();
    } catch (err) {
      setError(err.message || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId) => {
    if (!canRevoke || !activeTenantId) return;
    try {
      await accountApi.revokeApiKey(activeTenantId, keyId);
      await reloadKeys();
    } catch (err) {
      setError(err.message || 'Failed to revoke key');
    }
  };

  const handleRotate = async (keyId) => {
    if (!canRotate || !activeTenantId) return;
    setNewSecret(null);
    try {
      const rotated = await accountApi.rotateApiKey(activeTenantId, keyId);
      if (rotated.secret) {
        setNewSecret(rotated.secret);
      }
      await reloadKeys();
    } catch (err) {
      setError(err.message || 'Failed to rotate key');
    }
  };

  return (
    <div>
      <h1 className="product-page-title">Partner API Keys</h1>
      <p className="product-page-subtitle">
        Machine credentials for automation. Secrets are shown only once at creation or rotation.
      </p>

      {newSecret && (
        <div className="secret-once-banner">
          <strong>Copy this secret now — it will not be shown again.</strong>
          <code className="mono">{newSecret}</code>
          <Button
            size="sm"
            variant="outline-secondary"
            className="mt-2"
            onClick={() => setNewSecret(null)}
          >
            Dismiss
          </Button>
        </div>
      )}

      {error && <Alert variant="danger">{error}</Alert>}
      {loading && (
        <div className="loading-state">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading API keys…
        </div>
      )}

      {!loading && (
        <>
          <div className="product-card">
            <Table responsive className="product-table" size="sm">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Prefix</th>
                  <th>Status</th>
                  <th>Environment</th>
                  <th>Created</th>
                  {(canRevoke || canRotate) && <th />}
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id}>
                    <td>{k.name}</td>
                    <td className="mono">
                      {k.prefix}…{k.display_suffix}
                    </td>
                    <td>{k.status}</td>
                    <td>{k.environment}</td>
                    <td>{new Date(k.created_at).toLocaleDateString()}</td>
                    {(canRevoke || canRotate) && (
                      <td className="text-end">
                        {k.status === 'active' && canRotate && (
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            className="me-1"
                            onClick={() => handleRotate(k.id)}
                          >
                            Rotate
                          </Button>
                        )}
                        {k.status === 'active' && canRevoke && (
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleRevoke(k.id)}
                          >
                            Revoke
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {canCreate && (
            <div className="product-card">
              <h2 className="h6 mb-3">Create API key</h2>
              <Form onSubmit={handleCreate} className="row g-2 align-items-end">
                <div className="col-md-6">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="CI pipeline"
                  />
                </div>
                <div className="col-md-3">
                  <Button type="submit" disabled={creating}>
                    {creating ? 'Creating…' : 'Create key'}
                  </Button>
                </div>
              </Form>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ApiKeysPage;
